/**
 * RecentPatchesService - Track recently opened/saved patches
 * 
 * Uses ProfileSettingsBackend for storage:
 * - Logged OUT: localStorage
 * - Logged IN: profiles.settings.recents in Supabase
 * 
 * On login: migrate localStorage → cloud, clear localStorage
 */

import { RECENT_PATCHES_STORAGE_KEY, RECENT_PATCHES_MAX } from '../constants/index.js';
import { createProfileSettingsBackend } from './backends/ProfileSettingsBackend.js';

const CLOUD_KEY = 'recents';  // Key in profiles.settings
const RECENTS_MIGRATED_KEY = 'patchwerk-recents-migrated';

class RecentPatchesService {
    constructor() {
        this.authService = null;
        this.storageService = null;
        this.backend = null;
        this.syncTimeout = null;
        this.SYNC_DEBOUNCE_MS = 2000;
        this._recents = [];
        this._cloudLoaded = false;
        this._onCloudLoadedCallback = null;
        this._onErrorCallback = null;
    }

    setAuthService(authService) {
        this.authService = authService;
        this.backend = createProfileSettingsBackend(authService);
    }

    setStorageService(storageService) {
        this.storageService = storageService;
    }

    setOnCloudLoadedCallback(callback) {
        this._onCloudLoadedCallback = callback;
    }

    setOnErrorCallback(callback) {
        this._onErrorCallback = callback;
    }

    isCloudLoaded() {
        return this._cloudLoaded;
    }

    resetCloudLoadedState() {
        this._cloudLoaded = false;
        this._recents = [];
    }

    _isLoggedIn() {
        return this.backend?.isLoggedIn() || false;
    }

    /**
     * Get recent patches
     */
    getRecentPatches() {
        if (this._isLoggedIn()) {
            if (!this._cloudLoaded) {
                console.log('[RECENTS] getRecentPatches() - waiting for cloud');
                return [];
            }
            return this._recents.slice(0, RECENT_PATCHES_MAX);
        }

        // Local mode
        const stored = this.backend?.getLocal(RECENT_PATCHES_STORAGE_KEY);
        return (stored || []).slice(0, RECENT_PATCHES_MAX);
    }

    /**
     * Add a patch to recent patches
     */
    addToRecentPatches(patchId, name, timestamp, deviceCount, connectionCount) {
        if (!name || name === 'Untitled Patch') return;

        const entry = { patchId: patchId || '', name, timestamp };
        if (typeof deviceCount === 'number') entry.deviceCount = deviceCount;
        if (typeof connectionCount === 'number') entry.connectionCount = connectionCount;

        if (this._isLoggedIn()) {
            // Cloud mode
            const existingIdx = this._recents.findIndex(p => p.name === name);
            if (existingIdx !== -1) this._recents.splice(existingIdx, 1);
            
            this._recents.unshift(entry);
            if (this._recents.length > RECENT_PATCHES_MAX) this._recents.pop();
            
            this._cloudLoaded = true;
            this._syncToCloud();
        } else {
            // Local mode
            const recent = this.backend?.getLocal(RECENT_PATCHES_STORAGE_KEY) || [];
            
            const existingIdx = recent.findIndex(p => p.name === name);
            if (existingIdx !== -1) recent.splice(existingIdx, 1);
            
            recent.unshift(entry);
            if (recent.length > RECENT_PATCHES_MAX) recent.pop();

            this.backend?.setLocal(RECENT_PATCHES_STORAGE_KEY, recent);
        }
    }

    /**
     * Remove a patch from recents
     */
    removeFromRecents(patchId, name) {
        if (this._isLoggedIn()) {
            const before = this._recents.length;
            this._recents = this._recents.filter(p => {
                if (patchId && p.patchId === patchId) return false;
                if (!patchId && name && p.name === name) return false;
                return true;
            });
            
            if (this._recents.length < before) {
                console.log('[RECENTS] removeFromRecents() - syncing to cloud');
                this._syncToCloud();
            }
        } else {
            const recent = this.backend?.getLocal(RECENT_PATCHES_STORAGE_KEY);
            if (!recent) return;
            
            const filtered = recent.filter(p => {
                if (patchId && p.patchId === patchId) return false;
                if (!patchId && name && p.name === name) return false;
                return true;
            });
            
            if (filtered.length < recent.length) {
                this.backend?.setLocal(RECENT_PATCHES_STORAGE_KEY, filtered);
            }
        }
    }

    /**
     * Clear all recent patches
     */
    clearRecentPatches() {
        if (this._isLoggedIn()) {
            this._recents = [];
            this._syncToCloud();
        } else {
            this.backend?.removeLocal(RECENT_PATCHES_STORAGE_KEY);
        }
    }

    /**
     * Load recents from cloud on login
     */
    async loadFromCloud() {
        console.log('[RECENTS] loadFromCloud() - Starting');
        
        if (!this._isLoggedIn()) {
            console.log('[RECENTS] loadFromCloud() - Not logged in');
            return;
        }

        const userId = this.authService?.getCurrentUser()?.id;
        if (!userId) return;

        try {
            const migratedKey = `${RECENTS_MIGRATED_KEY}-${userId}`;
            const alreadyMigrated = localStorage.getItem(migratedKey) === 'true';
            
            // Read local data before clearing
            let localRecents = [];
            if (!alreadyMigrated) {
                localRecents = this.backend?.getLocal(RECENT_PATCHES_STORAGE_KEY) || [];
                console.log('[RECENTS] loadFromCloud() - Local recents to migrate:', localRecents.length);
            }

            // Fetch cloud data
            const cloudRecents = await this.backend.get(CLOUD_KEY) || [];
            console.log('[RECENTS] loadFromCloud() - Cloud recents:', cloudRecents.length);
            
            // First login with local data: migrate
            if (!alreadyMigrated && localRecents.length > 0) {
                console.log('[RECENTS] loadFromCloud() - Migrating local data');
                
                this._recents = this._mergeRecents(localRecents, cloudRecents);
                await this.backend.set(CLOUD_KEY, this._recents);
                
                this.backend.removeLocal(RECENT_PATCHES_STORAGE_KEY);
                localStorage.setItem(migratedKey, 'true');
                
                console.log('[RECENTS] loadFromCloud() - Migration complete:', this._recents.length);
            } else {
                this._recents = cloudRecents;
                if (!alreadyMigrated) localStorage.setItem(migratedKey, 'true');
            }
            
            this._cloudLoaded = true;
            
            // Validate recents against actual patches
            await this._validateRecents();
            
            // Notify UI
            if (this._onCloudLoadedCallback) {
                try { this._onCloudLoadedCallback(); } catch (e) {}
            }
        } catch (e) {
            console.error('[RECENTS] loadFromCloud() - Exception:', e);
            if (this._onErrorCallback) {
                this._onErrorCallback('Failed to sync recent patches');
            }
            this._cloudLoaded = true;
        }
    }

    /**
     * Validate recents against actual cloud patches
     */
    async _validateRecents() {
        if (!this._isLoggedIn() || !this.storageService) return;

        try {
            const patches = await this.storageService.listPatches();
            const validPatchIds = new Set(patches.map(p => p.id));
            
            const before = this._recents.length;
            this._recents = this._recents.filter(recent => {
                if (recent.patchId) {
                    return validPatchIds.has(recent.patchId);
                }
                return true;  // Local patches kept
            });
            
            if (this._recents.length < before) {
                console.log('[RECENTS] _validateRecents() - Removed', before - this._recents.length, 'phantom entries');
                await this.backend.set(CLOUD_KEY, this._recents);
            }
        } catch (e) {
            console.warn('[RECENTS] _validateRecents() - Exception:', e);
        }
    }

    /**
     * Merge local and cloud recents
     */
    _mergeRecents(local, cloud) {
        const byName = new Map();
        
        for (const entry of cloud) {
            if (entry.name) byName.set(entry.name, entry);
        }
        
        for (const entry of local) {
            if (!entry.name) continue;
            const existing = byName.get(entry.name);
            if (!existing || (entry.timestamp > existing.timestamp)) {
                byName.set(entry.name, entry);
            }
        }
        
        return Array.from(byName.values())
            .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
            .slice(0, RECENT_PATCHES_MAX);
    }

    /**
     * Sync to cloud (debounced)
     */
    _syncToCloud() {
        if (!this._isLoggedIn()) return;

        if (this.syncTimeout) clearTimeout(this.syncTimeout);
        
        this.syncTimeout = setTimeout(async () => {
            console.log('[RECENTS] _syncToCloud() - Pushing', this._recents.length, 'to cloud');
            await this.backend.set(CLOUD_KEY, this._recents);
        }, this.SYNC_DEBOUNCE_MS);
    }
}

export const recentPatchesService = new RecentPatchesService();
export { RecentPatchesService };
